import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { useConfig } from "../config/ConfigProvider";
import { getCurrentUser, logout as logoutService, refreshAccessToken, CurrentUser } from "../auth/oauthService";
import { Box } from "@mui/material";
import LoadingSpinner from "../components/common/LoadingSpinner";

interface OAuthContextType {
  user: CurrentUser | null;
  loading: boolean;
  error: string | null;
  logout: () => Promise<void>;
  refreshUser: (silent?: boolean) => Promise<void>;
}

const OAuthContext = createContext<OAuthContextType | undefined>(undefined);

export const OAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const config = useConfig();
  const apiUrl = config.apiBaseUrl;

  console.log('🎯 OAuthProvider render - user:', user ? `${user.email}` : 'null', 'loading:', loading, 'initialLoadComplete:', initialLoadComplete);

  const refreshUser = useCallback(async (silent: boolean = false) => {
    try {
      // Only show loading spinner on non-silent refreshes (and only if initial load not complete)
      if (!silent) {
        setLoading(true);
      }
      console.log('🔄 OAuthProvider.refreshUser - Starting... (silent:', silent, ')');
      const currentUser = await getCurrentUser(apiUrl);
      console.log('🔄 OAuthProvider.refreshUser - Got user:', currentUser);
      setUser(currentUser);
      console.log('🔄 OAuthProvider.refreshUser - User state updated');
      setError(null);
    } catch (err) {
      console.error("❌ OAuthProvider.refreshUser - Failed to fetch user:", err);
      setUser(null);
      setError(err instanceof Error ? err.message : "Failed to fetch user");
    } finally {
      if (!silent) {
        setLoading(false);
      }
      console.log('🔄 OAuthProvider.refreshUser - Loading set to false');
    }
  }, [apiUrl]);

  const logout = async () => {
    try {
      await logoutService(apiUrl);
      setUser(null);
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  useEffect(() => {
    // Fetch user on mount (checks accessToken cookie)
    refreshUser(false).then(() => {
      setInitialLoadComplete(true);
    });

    // Set up periodic token refresh (every 50 minutes, tokens expire in 60)
    const refreshInterval = setInterval(async () => {
      try {
        const success = await refreshAccessToken(apiUrl);
        if (success) {
          await refreshUser(true); // Silent refresh for automatic token refresh
        } else {
          // Refresh failed, user needs to re-authenticate
          setUser(null);
        }
      } catch (err) {
        console.error("Token refresh failed:", err);
        setUser(null);
      }
    }, 50 * 60 * 1000); // 50 minutes

    return () => clearInterval(refreshInterval);
  }, [apiUrl, refreshUser]);

  // Show loading spinner ONLY during initial authentication check
  // After initial load, allow children to render even during refreshes
  if (loading && !initialLoadComplete) {
    // Detect system dark mode preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    return (
      <OAuthContext.Provider value={{ user, loading, error, logout, refreshUser }}>
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: prefersDark ? "#121212" : "#ffffff",
          }}
        >
          <LoadingSpinner text="Loading" />
        </Box>
      </OAuthContext.Provider>
    );
  }

  return (
    <OAuthContext.Provider value={{ user, loading, error, logout, refreshUser }}>
      {children}
    </OAuthContext.Provider>
  );
};

export const useOAuth = (): OAuthContextType => {
  const context = useContext(OAuthContext);
  if (!context) {
    throw new Error("useOAuth must be used within OAuthProvider");
  }
  return context;
};

// Compatibility component to replace AuthenticatedTemplate
export const OAuthAuthenticatedTemplate: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ children, fallback }) => {
  const { user, loading } = useOAuth();

  console.log('🔍 OAuthAuthenticatedTemplate - loading:', loading, 'user:', user ? `${user.email} (${user.id})` : 'null');

  if (loading) {
    console.log('⏳ Still loading user - showing loading spinner');
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <LoadingSpinner text="Loading" />
      </Box>
    );
  }

  if (!user) {
    console.log('❌ No user - showing fallback (login page)');
    return <>{fallback}</>;
  }

  console.log('✅ User authenticated - showing protected content');
  return <>{children}</>;
};
